import { useQuery } from '@apollo/client';
import { PlayArrow } from '@mui/icons-material';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSendDeviceCommand } from '~/api/devices';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { ViewSelect } from '~/components/shows/view-select';
import { useAppContext } from '~/contexts';
import type { DeviceBulkSelectionInput } from '~/generated/graphql';
import { DeviceCommandKind } from '~/generated/graphql';
import { assert } from '~/lib/assert';
import { TriggerViewDialogDocument } from '../queries/queries.generated';

interface TriggerViewDialogProps {
  activeShowId: number;
  currentViewId?: number;
  onClose: () => void;
  selected: DeviceBulkSelectionInput;
}

export const TriggerViewDialog = ({
  activeShowId,
  currentViewId,
  onClose,
  selected,
}: TriggerViewDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data } = useQuery(TriggerViewDialogDocument, {
    variables: { networkId: currentNetwork.id, showId: activeShowId },
  });

  const show = data?.network?.show;

  const [viewId, setViewId] = useState<number | undefined>(currentViewId);

  const [sendCommand, { called }] = useSendDeviceCommand();

  const play = () => {
    const payload = show?.views.find((view) => view.id === viewId);
    assert(payload != null, 'TriggerViewDialog: No view selected');
    void sendCommand({
      variables: {
        input: { command: DeviceCommandKind.TriggerView, selected, payload },
      },
    }).then(onClose);
  };

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open>
      <DialogTitle onClose={onClose}>{t('pages.devices.dialogs.trigger_view.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>
          {t('pages.devices.dialogs.trigger_view.description')}
        </DialogContentTitle>

        <ViewSelect
          disabled={called}
          fullWidth
          onChange={setViewId}
          show={show}
          showThumbnail
          value={viewId}
        />
      </DialogContent>

      <DialogActions>
        <Button disabled={called} onClick={onClose} variant="outlined">
          {t('common.actions.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={viewId == null || called}
          onClick={play}
          startIcon={<PlayArrow />}
          variant="contained"
        >
          {t('pages.devices.dialogs.trigger_view.play_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
